import PropTypes from 'prop-types'
import { useMemo, useRef, useCallback, useState } from 'react'
import { Text, Tooltip } from '@artprov/design-system'
import { TABLAS_LABELS, TIPOS_DENUNCIAS } from '../../../common/constants/labels'

const toSentenceCase = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

const InfoCardConTooltip = ({ title, description, isLast = false }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const observerRef = useRef(null)

  const spanRef = useCallback((el) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    if (!el) return

    const check = () => setShowTooltip(el.scrollWidth > el.clientWidth)
    check()
    document.fonts?.ready?.then(() => requestAnimationFrame(check))

    observerRef.current = new ResizeObserver(check)
    observerRef.current.observe(el)
  }, [])

  return (
    <div className="min-h-[5rem] flex flex-col justify-between border border-tertiary-80 rounded-md px-3 py-2">
      <div className="mb-2">
        {showTooltip ? (
          <Tooltip
            title={title}
            position={isLast ? 'top' : 'bottom'}
            classNameTitle="!text-xs !font-medium"
            classNameCard="bg-white"
            classNameArrow="bg-white"
          >
            <span ref={spanRef} className="typography-b3 text-sm text-primary-10 block truncate">
              {title}
            </span>
          </Tooltip>
        ) : (
          <span ref={spanRef} className="typography-b3 text-sm text-primary-10 block truncate">
            {title}
          </span>
        )}
      </div>
      <div className="typography-b3 text-primary-30 text-lg sm:text-xl font-bold mt-2">{description}</div>
    </div>
  )
}

InfoCardConTooltip.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  isLast: PropTypes.bool,
}

/**
 * Componente para mostrar el ranking de denuncias agrupadas por tipo
 * @param {string} title - Título de la sección
 * @param {Array} data - Array de grupos de denuncias del API
 */
const RankingDenuncias = ({ title, data, isLoading }) => {
  const tiposCompletos = useMemo(() => {
    if (!data || isLoading) return []
    return TIPOS_DENUNCIAS.map((tipo) => {
      const dataTipo = data.find((d) => d.tipoId === tipo.tipoId)
      return {
        ...tipo,
        denuncias: dataTipo?.denuncias || [],
      }
    })
  }, [data, isLoading])

  if (isLoading) {
    return (
      <div className="w-full mt-6 border border-gray-300 rounded-md p-4 relative">
        <Text variant="h7" className="mt-5 mb-3 text-primary-10 font-semibold">
          {title}
        </Text>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {[0, 1, 2].map((col) => (
            <div key={col} className="flex flex-col gap-4">
              <div className="h-6 w-3/4 rounded bg-tertiary-80/30 animate-pulse" />
              <div className="flex flex-col gap-4">
                {[0, 1, 2, 3, 4].map((row) => (
                  <div key={row} className="min-h-[5rem] w-full rounded-md bg-tertiary-80/30 animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="absolute inset-0 flex items-center justify-center rounded-md">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-primary" />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mt-6 border border-gray-300 rounded-md p-3">
      <Text variant="h7" className="mt-5 mb-3 text-primary-10 font-semibold">
        {title}
      </Text>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
        {tiposCompletos.map((grupo) => (
          <div key={grupo.tipoId} className="flex flex-col gap-4">
            <Text variant="body1" className="font-semibold text-primary-10">
              {toSentenceCase(grupo.tipoDescripcion)}
            </Text>
            <div className="flex flex-col gap-4">
              {grupo.denuncias.length > 0 ? (
                grupo.denuncias.map((denuncia, index) => (
                  <InfoCardConTooltip
                    key={`${grupo.tipoId}-${index}`}
                    title={toSentenceCase(denuncia.descripcion)}
                    description={String(denuncia.cantidad)}
                    isLast={grupo.denuncias.length === 5 && index === 4}
                  />
                ))
              ) : (
                <div className="min-h-[5rem] flex items-center border border-tertiary-80 rounded-md px-3 py-2 bg-tertiary-98">
                  <p className="typography-b3 text-tertiary-40 px-2">
                    <span className="font-bold">Sin datos</span> {TABLAS_LABELS.NO_DATA_PERIOD}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

RankingDenuncias.propTypes = {
  title: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      tipoId: PropTypes.number.isRequired,
      tipoDescripcion: PropTypes.string.isRequired,
      denuncias: PropTypes.arrayOf(
        PropTypes.shape({
          descripcion: PropTypes.string.isRequired,
          cantidad: PropTypes.number.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
}

export default RankingDenuncias
